/**
 * Project paintings, one per catalogue entry.
 *
 * Files live in `public/images/projects/<slug>-480.webp` and `-800.webp`
 * (3:2 source, 480x320 and 800x534). `PROJECT_ART_VERSIONS` below is the ONLY
 * registry: a slug that is not listed gets the plain blue header, so a project
 * added to `projects.json` before its painting exists renders exactly as it did
 * before and nothing 404s. `projectArt.test.ts` keeps the registry, the folder and
 * the hashes in step.
 *
 * Cache-busting: the server sends `.webp` files `immutable` for a year, so a
 * re-rolled painting saved over the same file name would never reach a returning
 * visitor. Every URL therefore carries `?v=<first 8 hex of the file's SHA-256>`.
 * Re-run the import script after changing any file; it rewrites the block below,
 * and the test fails if a hash is stale.
 *
 * Decorative by design: the paintings illustrate an idea, not the client, and
 * carry no information the card text does not, so callers render them `alt=""`.
 */
export interface ProjectArt {
  src480: string;
  src800: string;
  /** Intrinsic size of the 480w file; the 800w file has the same 3:2 ratio. */
  width: number;
  height: number;
}

const ART_DIR = '/images/projects';

/* <generated:art-versions> slug -> [480w hash, 800w hash]. Written by import_art.py. */
export const PROJECT_ART_VERSIONS: Readonly<Record<string, readonly [string, string]>> = {
  'ai-accounts-payable-categorisation': ['8300fa72', 'a9d97503'],
  'ai-quoting-engine-for-industrial-distribution': ['37038618', '1f3af776'],
  'applicant-screening-and-assessment': ['d7855567', '68403a90'],
  'audio-visual-network-segmentation': ['ca04afa6', '2ba40fb1'],
  'compass-service-management-platform': ['cc7e1eae', '5a18b692'],
  'configurable-product-web-store': ['a5ea751c', '1c833b38'],
  'crm-across-parent-and-subsidiary': ['9b63dc56', 'd2e97543'],
  'cross-tenant-identity-synchronisation': ['0dde5235', 'acb34473'],
  'davinci-erp-platform': ['5e9159b7', 'a70bb57f'],
  'endpoint-detection-and-response-rollout': ['2512f5d1', '0c577940'],
  'estimating-tool-dynamics-integration': ['6071f3e6', 'd8708b5d'],
  'fleet-data-intelligence-platform': ['f7987064', '19c924b6'],
  'homebuilder-sales-operations-platform': ['59bced45', '18ee85bb'],
  'hris-across-five-operating-companies': ['e104477d', '6c7e9ef5'],
  'hyper-v-to-azure-site-recovery-migration': ['bbc811af', 'b7a29909'],
  'job-margin-visibility-integration': ['40e4482b', '72dc1db6'],
  'kpi-scorecard-automation': ['8e835914', '7ea80b12'],
  'legacy-pbx-to-cloud-calling': ['3a88a386', '12caa47f'],
  'leo-ai-project-reporting-agent': ['31319901', 'b8b73169'],
  'measuring-ai-reliability-before-improving-it': ['32fa5677', '8b96e0d0'],
  'model-context-protocol-erp-integration': ['bb3897d7', 'd71cd4ca'],
  'multi-company-azure-migration': ['55c953f6', 'a687e7af'],
  'multi-tenant-project-operations-platform': ['5b5a3279', 'd52e8c81'],
  'multimodal-ai-for-creative-decisions': ['b9abb1c1', '9ca14d31'],
  'new-entity-microsoft-365-foundation': ['3472aa71', 'dd993f6b'],
  'non-profit-tenant-deployment': ['69d39c3a', 'ba6262d1'],
  'ongoing-dynamics-platform-partnership': ['193657ed', 'a6a8d3a7'],
  'pharmacy-network-and-workstation-buildout': ['a8024cd4', 'f5876d4d'],
  'private-ai-chat-platform': ['b9f7636c', '5ed2e53e'],
  'review-platform-automation': ['799bbca8', '722cccbe'],
  'sentiment-and-proficiency-scoring': ['3ec454f1', '847dfc45'],
  'teams-phone-migration': ['4f7b7eaf', '8c0272c7'],
  'teams-phone-with-call-queues': ['7369580b', 'c96636e4'],
  'ticket-classification-and-routing': ['bcbcf4f1', '2e0313e6'],
  'tier-zero-support-agent': ['8c8dd2ef', '1522f615'],
  'unified-mac-and-windows-management': ['3fc982fb', 'ef5de903'],
};
/* </generated:art-versions> */

export const PROJECT_ART_SLUGS: readonly string[] = Object.keys(PROJECT_ART_VERSIONS);

const ART_SLUG_ALIASES: Record<string, string> = {
  'davinci-erp': 'davinci-erp-platform',
  'compass-mcp-integration': 'model-context-protocol-erp-integration',
  'fleet-management-tool': 'fleet-data-intelligence-platform',
  'accounting-dashboard': 'kpi-scorecard-automation',
  'document-creator': 'multi-tenant-project-operations-platform',
  'code-pairing-graph-api-tool': 'cross-tenant-identity-synchronisation',
  'canadian-Hours-of-Service-Calculator': 'homebuilder-sales-operations-platform',
  'alberta-herbicide-calculator': 'estimating-tool-dynamics-integration',
  'restaurant-management-app': 'configurable-product-web-store',
  'boardwalk-alert-watcher': 'measuring-ai-reliability-before-improving-it',
};

export const getProjectArt = (slug: string | undefined): ProjectArt | null => {
  const resolved = slug ? (PROJECT_ART_VERSIONS[slug] ? slug : ART_SLUG_ALIASES[slug]) : undefined;
  const versions = resolved ? PROJECT_ART_VERSIONS[resolved] : undefined;
  if (!resolved || !versions) return null;
  return {
    src480: `${ART_DIR}/${resolved}-480.webp?v=${versions[0]}`,
    src800: `${ART_DIR}/${resolved}-800.webp?v=${versions[1]}`,
    width: 480,
    height: 320,
  };
};
